<template>
  <div class="groups-manager">
    <div class="groups-toolbar">
      <button type="button" class="btn-new-group" @click="handleCreateGroup">+ New group</button>
    </div>

    <div class="groups-layout">
      <section class="ungrouped-column" aria-label="Ungrouped">
        <header class="column-header">
          <h3 class="column-title">Ungrouped</h3>
          <span class="column-count">{{ ungrouped.length }}</span>
        </header>
        <draggable
          :list="ungrouped"
          item-key="id"
          :group="{ name: dndGroupName, pull: true, put: true }"
          class="item-list"
          ghost-class="drag-ghost"
          @add="onRemoveFromGroup($event)"
        >
          <template #item="{ element }">
            <div class="item-card">
              <span class="item-title">{{ element.displayName || element[itemNameField] }}</span>
              <span class="item-subtitle">{{ element[itemNameField] }}</span>
            </div>
          </template>
        </draggable>
      </section>

      <div class="group-panels">
        <article v-for="group in groups" :key="group.id" class="group-panel">
          <header class="group-header">
            <input
              v-if="editingGroupId === group.id"
              v-model="editingName"
              class="group-name-input"
              :ref="el => focusOnMount(el, editingGroupId === group.id)"
              @keydown.enter="commitRename(group)"
              @keydown.esc="cancelRename"
              @blur="commitRename(group)"
            />
            <h3
              v-else
              class="group-name"
              tabindex="0"
              @click="startRename(group)"
              @keydown.enter="startRename(group)"
            >
              {{ group.name }}
            </h3>
            <button
              type="button"
              class="btn-delete"
              :aria-label="`Delete group ${group.name}`"
              @click="askDelete(group)"
            >
              ×
            </button>
          </header>
          <draggable
            :list="group.items"
            item-key="id"
            :group="{ name: dndGroupName, pull: true, put: true }"
            class="item-list item-list--drop"
            ghost-class="drag-ghost"
            @add="onAddToGroup($event, group.id)"
          >
            <template #item="{ element }">
              <div class="item-card">
                <span class="item-title">{{ element.displayName || element[itemNameField] }}</span>
                <span class="item-subtitle">{{ element[itemNameField] }}</span>
              </div>
            </template>
          </draggable>
          <p v-if="!group.items.length" class="empty-hint">
            Drag a {{ itemNounSingular }} here to add it to the group.
          </p>
        </article>
      </div>
    </div>

    <CreateGroupModal
      :open="showCreateModal"
      :error="createError"
      :busy="createBusy"
      @confirm="confirmCreate"
      @cancel="cancelCreate"
    />

    <DeleteGroupModal
      :open="deleteTarget != null"
      :group-name="deleteTarget?.name || ''"
      :member-count="deleteTarget?.items?.length ?? 0"
      :kind="kind"
      @confirm="confirmDelete"
      @cancel="deleteTarget = null"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import draggable from 'vuedraggable';
import DeleteGroupModal from './DeleteGroupModal.vue';
import CreateGroupModal from './CreateGroupModal.vue';

const props = defineProps({
  databaseDisplayName: { type: String, required: true },
  service: { type: Object, required: true },
  items: { type: Array, default: () => [] },
  kind: { type: String, default: 'roiProcess' },
});
const emit = defineEmits(['changed']);

const isQueue = computed(() => props.kind === 'roiQueue');
const itemNameField = computed(() => (isQueue.value ? 'queueName' : 'processName'));
const itemNounSingular = computed(() => (isQueue.value ? 'queue' : 'process'));
const itemNounError = computed(() => (isQueue.value ? 'queue' : 'process'));
const dndGroupName = computed(() => (isQueue.value ? 'queue-items' : 'process-items'));

const groups = ref([]);
const localItems = ref([]);
const editingGroupId = ref(null);
const editingName = ref('');
const deleteTarget = ref(null);
const showCreateModal = ref(false);
const createError = ref('');
const createBusy = ref(false);

const ungrouped = ref([]);

function rebuildPartitions() {
  ungrouped.value.length = 0;
  for (const g of groups.value) g.items.length = 0;
  for (const item of localItems.value) {
    const g = groups.value.find(x => x.id === item.groupId);
    if (g) g.items.push(item);
    else ungrouped.value.push(item);
  }
}

function syncItemsFromProps() {
  localItems.value = props.items.map(i => ({ ...i }));
  rebuildPartitions();
}

async function loadGroups() {
  const fetched = await props.service.getGroups(props.databaseDisplayName);
  groups.value = fetched.map(g => ({ ...g, items: [] }));
  rebuildPartitions();
}

function handleCreateGroup() {
  createError.value = '';
  showCreateModal.value = true;
}

function cancelCreate() {
  showCreateModal.value = false;
  createError.value = '';
}

async function confirmCreate(name) {
  createError.value = '';
  createBusy.value = true;
  try {
    const created = await props.service.createGroup(props.databaseDisplayName, name);
    if (!created) {
      createError.value = 'That name is already in use';
      return;
    }
    groups.value.push({ ...created, items: [] });
    showCreateModal.value = false;
  } catch (e) {
    createError.value = e?.message || 'Could not create group';
  } finally {
    createBusy.value = false;
  }
}

function startRename(group) {
  editingGroupId.value = group.id;
  editingName.value = group.name;
}

function cancelRename() {
  editingGroupId.value = null;
  editingName.value = '';
}

async function commitRename(group) {
  if (editingGroupId.value !== group.id) return;
  const newName = editingName.value.trim();
  editingGroupId.value = null;
  if (!newName || newName === group.name) return;
  try {
    const updated = await props.service.renameGroup(props.databaseDisplayName, group.id, newName);
    if (!updated) {
      window.$notify?.('That name is already in use', '', 'error');
      return;
    }
    Object.assign(group, updated);
  } catch (e) {
    window.$notify?.('Could not rename group', e?.message || '', 'error');
  }
}

function askDelete(group) {
  deleteTarget.value = group;
}

async function confirmDelete() {
  const target = deleteTarget.value;
  deleteTarget.value = null;
  if (!target) return;
  try {
    await props.service.deleteGroup(props.databaseDisplayName, target.id);
    const orphaned = target.items ?? [];
    for (const item of orphaned) { item.groupId = null; item.groupName = null; }
    ungrouped.value.push(...orphaned);
    groups.value = groups.value.filter(g => g.id !== target.id);
    emit('changed');
  } catch (e) {
    window.$notify?.('Could not delete group', e?.message || '', 'error');
  }
}

async function onRemoveFromGroup(evt) {
  const moved = evt.item?.__draggable_context?.element;
  if (!moved) return;
  const previousGroupId = moved.groupId ?? null;
  moved.groupId = null;
  moved.groupName = null;
  try {
    await props.service.setMembership(props.databaseDisplayName, moved.id, null);
    emit('changed');
  } catch (e) {
    const idx = ungrouped.value.findIndex(i => i.id === moved.id);
    const prevGroup = groups.value.find(g => g.id === previousGroupId);
    if (idx > -1 && prevGroup) {
      const [back] = ungrouped.value.splice(idx, 1);
      prevGroup.items.push(back);
    }
    moved.groupId = previousGroupId;
    moved.groupName = prevGroup?.name ?? null;
    window.$notify?.(`Could not move ${itemNounError.value}`, e?.message || '', 'error');
  }
}

async function onAddToGroup(evt, groupId) {
  const moved = evt.item?.__draggable_context?.element;
  if (!moved) return;
  const previousGroupId = moved.groupId ?? null;
  const destGroup = groups.value.find(g => g.id === groupId);
  moved.groupId = groupId;
  moved.groupName = destGroup?.name ?? null;
  try {
    await props.service.setMembership(props.databaseDisplayName, moved.id, groupId);
    emit('changed');
  } catch (e) {
    const idx = destGroup?.items.findIndex(i => i.id === moved.id) ?? -1;
    if (idx > -1) {
      const [back] = destGroup.items.splice(idx, 1);
      const prevGroup = groups.value.find(g => g.id === previousGroupId);
      if (prevGroup) prevGroup.items.push(back);
      else ungrouped.value.push(back);
    }
    moved.groupId = previousGroupId;
    moved.groupName = groups.value.find(g => g.id === previousGroupId)?.name ?? null;

    if (e?.status === 404) {
      window.$notify?.('The group no longer exists', '', 'error');
      await loadGroups();
      emit('changed');
    } else {
      window.$notify?.(`Could not move ${itemNounError.value}`, e?.message || '', 'error');
    }
  }
}

function focusOnMount(el, shouldFocus) {
  if (el && shouldFocus) {
    nextTick(() => el.focus?.());
  }
}

// Re-sync when the parent supplies a fresh item list (e.g. after creating a
// new ROI entry), so it shows up in the Ungrouped column immediately —
// matching the list view, which re-populates on the same data refresh.
watch(() => props.items, syncItemsFromProps);

onMounted(async () => {
  syncItemsFromProps();
  await loadGroups();
});

defineExpose({
  reload: async () => {
    syncItemsFromProps();
    await loadGroups();
  },
});
</script>

<style scoped>
.groups-manager {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.groups-toolbar {
  display: flex;
  justify-content: flex-end;
}

.btn-new-group {
  padding: 0.5rem 1rem;
  background: var(--highlights, #2563eb);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
}

.btn-new-group:hover {
  filter: brightness(0.95);
}

.groups-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1rem;
  align-items: start;
}

.ungrouped-column,
.group-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem;
}

.column-header,
.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
}

.column-title,
.group-name {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  min-width: 0;
  flex: 1;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.column-count {
  font-size: 0.8rem;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
}

.group-name-input {
  font-size: 0.95rem;
  font-weight: 600;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0.2rem 0.4rem;
  flex: 1;
  min-width: 0;
}

.btn-delete {
  background: transparent;
  border: none;
  font-size: 1.25rem;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 0 0.25rem;
}

.btn-delete:hover {
  color: #dc2626;
}

.group-panels {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.item-list {
  min-height: 60px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.25rem;
  border: 1px dashed transparent;
  border-radius: 8px;
}

.item-list--drop {
  border-color: #e2e8f0;
}

.item-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.5rem 0.65rem;
  cursor: grab;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
  max-width: 100%;
}

.item-card:active {
  cursor: grabbing;
}

.item-title {
  font-weight: 500;
  color: #0f172a;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.item-subtitle {
  font-size: 0.75rem;
  color: #64748b;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.drag-ghost {
  opacity: 0.4;
  background: #dbeafe;
}

.empty-hint {
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: #94a3b8;
  font-style: italic;
}
</style>
